<template>
  <div class="store-group-mud-panel form-container panel-box">
    <span class="form-header mb-3">{{ $tkey('panelTitle') }}</span>

    <!-- Offset type toggle: Relative % ↔ Absolute currency -->
    <div class="d-flex align-center mb-3 mt-2">
      <span class="offset-type-label font-weight-600 mr-3">
        {{ $tkey('relative') }}
        {{ $t('general.percentSymbol') }}
      </span>
      <v-switch :input-value="isAbsolute" class="ma-0" hide-details @change="onOffsetTypeChange" />
      <span class="offset-type-label font-weight-600 ml-3">
        {{ $tkey('absolute') }}
        {{ $t('clientCurrencySymbol') }}
      </span>
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-icon small v-bind="attrs" class="info-icon ml-2" v-on="on"
            >mdi-information-outline</v-icon
          >
        </template>
        <span>{{ isRelative ? $tkey('relativeOffsetHint') : $tkey('absoluteOffsetHint') }}</span>
      </v-tooltip>
    </div>

    <!-- Max Up Direction -->
    <v-row class="mud-row" align="center" no-gutters>
      <v-col cols="12" md="auto" class="mud-input">
        <v-text-field
          v-model="maxUpDisplay"
          :label="$t('settings.penaltyRules.storeGroupMaxUpDown.maxUp')"
          type="text"
          flat
          :background-color="owWhite"
          :suffix="unitLabel"
          :error-messages="maxUpDown.applyMaxUp ? validationErrors.maxUpValue : []"
          :disabled="!maxUpDown.applyMaxUp"
          class="max-up-down-input"
          @keypress="ensureDecimal($event, true)"
          @paste="ensurePastedDecimal($event, true)"
        />
      </v-col>
      <v-col cols="12" md="auto" class="mud-control">
        <v-checkbox
          v-model="applyMaxUp"
          class="pa-0 ma-0"
          :label="$t('settings.penaltyRules.Active')"
        />
      </v-col>
    </v-row>

    <!-- Max Down Direction -->
    <v-row class="mud-row" align="center" no-gutters>
      <v-col cols="12" md="auto" class="mud-input">
        <v-text-field
          v-model="maxDownDisplay"
          :label="$t('settings.penaltyRules.storeGroupMaxUpDown.maxDown')"
          type="text"
          flat
          :background-color="owWhite"
          :suffix="unitLabel"
          :error-messages="maxUpDown.applyMaxDown ? validationErrors.maxDownValue : []"
          :disabled="!maxUpDown.applyMaxDown"
          class="max-up-down-input"
          @keypress="ensureDecimal($event, true)"
          @paste="ensurePastedDecimal($event, true)"
        />
      </v-col>
      <v-col cols="12" md="auto" class="mud-control">
        <v-checkbox
          v-model="applyMaxDown"
          class="pa-0 ma-0"
          :label="$t('settings.penaltyRules.Active')"
        />
      </v-col>
    </v-row>

    <!-- Validation error: at least one direction must be enabled -->
    <div v-if="validationErrors.maxUpDownDirection" class="d-flex flex-column mt-2">
      <span class="validation-fail pl-2">{{ validationErrors.maxUpDownDirection }}</span>
    </div>
  </div>
</template>

<script>
import { isFinite } from 'lodash';
import colours from '../../../../ow-colors';
import formatNumberMixin from '@/js/mixins/formatNumber';
import ensureNumberMixin from '@/js/mixins/ensureNumber';
import numberFormats from '@enums/number-formats';

/**
 * Store Group Max Up/Down panel.
 * Renders the offset-type toggle and max-up/max-down input rows for a store group penalty.
 * Mutates `maxUpDown` on the penalty object passed in via `selectedStoreGroupPenalty`.
 */
export default {
  localizationKey: 'settings.penaltyRules.storeGroupMaxUpDown',

  mixins: [formatNumberMixin, ensureNumberMixin],

  props: {
    /** The full penalty object for the selected store group (contains the maxUpDown block). */
    selectedStoreGroupPenalty: {
      type: Object,
      required: true,
    },
  },

  emits: ['validationChange'],

  data() {
    return {
      owWhite: colours.owWhite,
      numberFormats,
    };
  },

  computed: {
    maxUpDown() {
      return this.selectedStoreGroupPenalty.maxUpDown;
    },

    /** Derives current validation errors from the maxUpDown state. */
    validationErrors() {
      const errors = {};
      const sg = this.selectedStoreGroupPenalty;
      if (sg && sg.isActive && sg.maxUpDown && sg.maxUpDown.isActive) {
        const mud = sg.maxUpDown;
        if (!mud.offsetType) {
          errors.maxUpDownUnit = this.$tkey('errors.offsetTypeRequired');
        }
        // At least one direction must be enabled
        if (!mud.applyMaxUp && !mud.applyMaxDown) {
          errors.maxUpDownDirection = this.$tkey('errors.directionRequired');
        }
        // If Max Up is enabled, value is required and must be non-negative
        if (mud.applyMaxUp) {
          if (!isFinite(mud.maxUp)) {
            errors.maxUpValue = this.$tkey('errors.maxUpValueRequired');
          } else if (mud.maxUp < 0) {
            errors.maxUpValue = this.$tkey('errors.maxUpNonNegative');
          }
        }
        // If Max Down is enabled, value is required and must be non-negative
        if (mud.applyMaxDown) {
          if (!isFinite(mud.maxDown)) {
            errors.maxDownValue = this.$tkey('errors.maxDownValueRequired');
          } else if (mud.maxDown < 0) {
            errors.maxDownValue = this.$tkey('errors.maxDownNonNegative');
          }
        }
      }
      return errors;
    },

    isAbsolute() {
      return this.maxUpDown.offsetType === 'abs';
    },

    isRelative() {
      return this.maxUpDown.offsetType === 'pct';
    },

    unitLabel() {
      return this.isRelative ? this.$t('general.percentSymbol') : this.$t('clientCurrencySymbol');
    },

    applyMaxUp: {
      get() {
        return this.maxUpDown.applyMaxUp;
      },
      set(value) {
        this.$set(this.maxUpDown, 'applyMaxUp', value);
        if (!value) {
          this.$set(this.maxUpDown, 'maxUp', null);
        }
      },
    },

    applyMaxDown: {
      get() {
        return this.maxUpDown.applyMaxDown;
      },
      set(value) {
        this.$set(this.maxUpDown, 'applyMaxDown', value);
        if (!value) {
          this.$set(this.maxUpDown, 'maxDown', null);
        }
      },
    },

    maxUp: {
      get() {
        return this.maxUpDown.maxUp;
      },
      set(value) {
        this.$set(this.maxUpDown, 'maxUp', value);
      },
    },

    maxUpDisplay: {
      get() {
        if (this.maxUp === null || this.maxUp === undefined) return '';
        return this.formatNumber({ number: this.maxUp, format: numberFormats.decimal });
      },
      set(value) {
        const parsed = this.formatStringToNumber(value);
        this.maxUp = parsed !== null ? parsed : null;
      },
    },

    maxDown: {
      get() {
        return this.maxUpDown.maxDown;
      },
      set(value) {
        this.$set(this.maxUpDown, 'maxDown', value);
      },
    },

    maxDownDisplay: {
      get() {
        if (this.maxDown === null || this.maxDown === undefined) return '';
        return this.formatNumber({ number: this.maxDown, format: numberFormats.decimal });
      },
      set(value) {
        const parsed = this.formatStringToNumber(value);
        this.maxDown = parsed !== null ? parsed : null;
      },
    },
  },

  watch: {
    /** Emits the current validation state whenever errors change. */
    validationErrors: {
      handler(errors) {
        this.$emit('validationChange', {
          isValid: Object.keys(errors).length === 0,
          errors,
        });
      },
      deep: true,
      immediate: true,
    },
  },

  beforeDestroy() {
    // Clear any errors in the parent when this panel is unmounted.
    this.$emit('validationChange', { isValid: true, errors: {} });
  },

  methods: {
    /** Toggles the offset type between relative (pct) and absolute (abs). */
    onOffsetTypeChange(isAbsolute) {
      const offsetType = isAbsolute ? 'abs' : 'pct';
      this.$set(this.maxUpDown, 'offsetType', offsetType);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.form-container {
  .form-header {
    padding: 0.2rem 1.2rem;
    border-radius: 0.5rem;
    font-size: 1.2rem;
    background-color: $paramsFormHeaderBackgroundColor;
    color: $pricing-white;
    display: inline-block;
  }
}

.panel-box {
  background-color: $paramsFormBackgroundColor;
  padding: 1.5rem;
}
.mud-row {
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}

.mud-control {
  flex: 0 0 auto;
  display: flex;
  margin-top: 1.4rem;
}

.mud-input {
  flex: 0 0 auto;
  min-width: 12rem;
}

.store-group-mud-panel {
  flex: 0 0 auto;
  max-width: 26rem;
  align-self: stretch;
}

.validation-fail {
  color: red;
  font-size: 1.2rem;
}

.offset-type-label {
  font-size: 1.2rem;
}

.info-icon {
  color: #757575;
  cursor: help;
}
</style>
